import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, Pencil, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/sonner'
import { INPUT_CLASSES } from '@/lib/theme'
import { cn } from '@/lib/utils'
import athleteApi from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { themesOf } from '@/lib/athleteScore'
import SetupShell, { SectionTitle } from '@/components/setup/SetupShell'
import {
  type ContentTheme,
  DELIVERABLES,
  INCLUDED_LICENSE_DAYS,
  ITEM_UNITS,
  LICENSE_TERMS,
  MAX_ITEMS,
  licenseSavings,
  type ItemUnit,
  formatRange,
  suggestedLicensing,
  videoPriceOf,
  type DeliverableId,
  type LicenseTerm,
  type RateCard as Card,
} from '@adthlete/common/utils/dealPricing'

/** One of their own items, as edited (a new one has no id until it's saved) */
type Item = {
  id?: string
  label: string
  description: string
  unit: ItemUnit
  low: number
  high: number
}

type Draft = {
  themes: ContentTheme[]
  items: Item[]
  deliverables: Record<DeliverableId, { low: number; high: number; custom: boolean }>
  licensing: Record<LicenseTerm, { amount: number; custom: boolean }>
}

const toDraft = (c: Card): Draft => ({
  themes: c.themes ?? [],
  items: c.items ?? [],
  deliverables: c.deliverables,
  licensing: c.licensing,
})

const PriceRow: React.FC<{
  label: string
  sub: string
  suggested: string
  suggestedNote?: string
  custom: boolean
  value: number
  onCustom: (on: boolean) => void
  onValue: (n: number) => void
}> = ({
  label,
  sub,
  suggested,
  suggestedNote = 'Suggested for athletes like you',
  custom,
  value,
  onCustom,
  onValue,
}) => (
  <li className="rounded-2xl border border-foreground/10 bg-card p-4 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-bold text-foreground">{label}</p>
        <p className="text-xs text-gray-600">{sub}</p>
      </div>
      <p
        className={cn(
          'shrink-0 text-right text-lg font-bold tabular-nums',
          custom ? 'text-gray-500 line-through decoration-1' : 'text-[#4d7c0f]',
        )}
      >
        {suggested}
      </p>
    </div>
    {custom ? (
      <div className="mt-3 flex items-center gap-2">
        <span className="text-lg font-semibold text-foreground">$</span>
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          value={value || ''}
          onChange={(e) => onValue(Number(e.target.value))}
          aria-label={`Your price for ${label}`}
          className={cn(INPUT_CLASSES.dark, 'h-11 text-base tabular-nums')}
        />
        <button
          type="button"
          onClick={() => onCustom(false)}
          className="shrink-0 rounded-full px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-foreground/10"
        >
          Use suggested
        </button>
      </div>
    ) : (
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-[#4d7c0f]">
          <Check className="h-3.5 w-3.5" strokeWidth={3} /> {suggestedNote}
        </span>
        <button
          type="button"
          onClick={() => onCustom(true)}
          className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-foreground/10"
        >
          <Pencil className="h-3.5 w-3.5" /> Set my own
        </button>
      </div>
    )}
  </li>
)

/**
 * "Set your rate card": what the athlete charges per deliverable and for licensing their
 * videos as paid ads. Each price starts at the range athletes like them usually get; they
 * keep it or set their own. Brands see this card and book from it, which cuts out most of the
 * back-and-forth on a deal.
 */
const RateCardPage: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, refreshUser } = useAuth()
  const themes = themesOf((athlete ?? {}) as any)
  const [suggested, setSuggested] = useState<Card | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    athleteApi
      .get('/athlete/rate-card')
      .then(({ data }) => {
        setSuggested(data.suggested)
        setDraft(toDraft(data.rateCard ?? data.suggested))
      })
      .catch(() => toast.error("Couldn't load your rates. Try again in a moment."))
  }, [])

  // Licensing suggestions follow the Reel price the athlete settles on
  const licenseSuggestion = useMemo(
    () => (draft ? suggestedLicensing(videoPriceOf(draft)) : null),
    [draft],
  )

  if (!draft || !suggested || !licenseSuggestion) {
    return (
      <SetupShell title="Your rate card">
        <p className="text-sm text-gray-600">Loading…</p>
      </SetupShell>
    )
  }

  const setDeliverable = (
    id: DeliverableId,
    patch: Partial<Draft['deliverables'][DeliverableId]>,
  ) =>
    setDraft(
      (d) =>
        d && {
          ...d,
          deliverables: { ...d.deliverables, [id]: { ...d.deliverables[id], ...patch } },
        },
    )
  // The extension prices as they stand, for the "% off" line under each
  const currentLicensing = Object.fromEntries(
    LICENSE_TERMS.map((t) => {
      const e = draft.licensing[t.days]
      return [t.days, e.custom ? e.amount : licenseSuggestion[t.days]]
    }),
  ) as Record<LicenseTerm, number>
  const savings = (days: LicenseTerm) => licenseSavings(currentLicensing, days)

  const setLicense = (days: LicenseTerm, patch: Partial<Draft['licensing'][LicenseTerm]>) =>
    setDraft(
      (d) =>
        d && { ...d, licensing: { ...d.licensing, [days]: { ...d.licensing[days], ...patch } } },
    )

  const save = async () => {
    // Suggested entries take the current suggestion (licensing follows the Reel price)
    if (!themes.length) {
      toast.error('Pick your content themes first', { description: 'What do you post most?' })
      return
    }
    const bad = draft.items.find((it) => !it.label.trim() || !(it.low > 0))
    if (bad) {
      toast.error(bad.label.trim() ? `Add a price for ${bad.label}` : 'Give each item a name')
      return
    }
    const body: Omit<Draft, 'themes'> = {
      // A single price is a range of one
      items: draft.items.map((it) => ({ ...it, high: Math.max(it.high, it.low) })),
      deliverables: Object.fromEntries(
        DELIVERABLES.map((x) => {
          const e = draft.deliverables[x.id]
          return [
            x.id,
            e.custom
              ? { low: e.low, high: e.low, custom: true }
              : { ...suggested.deliverables[x.id], custom: false },
          ]
        }),
      ) as Draft['deliverables'],
      licensing: Object.fromEntries(
        LICENSE_TERMS.map((t) => {
          const e = draft.licensing[t.days]
          return [t.days, e.custom ? e : { amount: licenseSuggestion[t.days], custom: false }]
        }),
      ) as Draft['licensing'],
    }
    setSaving(true)
    try {
      await athleteApi.put('/athlete/rate-card', body)
      await refreshUser()
      toast('Rate card saved', { description: 'Brands see these prices when they look at you.' })
      navigate('/brand-view')
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Couldn't save. Check your prices.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <SetupShell
      title="Your rate card"
      subtitle={
        <div>
          <SectionTitle>Set your rates</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            Suggested starting prices for athletes like you. Keep them or set your own.
          </p>
        </div>
      }
      footer={
        <Button variant="lime" className="h-12 w-full text-base" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save my rate card'}
        </Button>
      }
      footnote="Brands book you at these prices, so most deals need no back-and-forth."
    >
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-bold text-foreground">What brands can book</p>
          <Link
            to="/setup/themes"
            className="inline-flex items-center gap-1 text-sm font-semibold text-[#3F7A0A]"
          >
            <Pencil className="h-3.5 w-3.5" /> {themes.length ? 'Edit themes' : 'Pick themes'}
          </Link>
        </div>
        <p className="mb-3 text-sm text-gray-600">
          {themes.length
            ? 'Your content themes. Brands book these at your rates; anything else is a custom request.'
            : 'Pick your content themes first: the formats you post again and again.'}
        </p>
        {themes.length ? (
          <div className="flex flex-wrap gap-2">
            {themes.map((t) => (
              <span
                key={t.id}
                className="rounded-full border border-foreground/15 px-3 py-1.5 text-sm font-semibold text-gray-800"
              >
                {t.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <p className="pt-2 font-bold text-foreground">Your rates</p>
      <ul className="space-y-3">
        {DELIVERABLES.map((d) => {
          const e = draft.deliverables[d.id]
          return (
            <PriceRow
              key={d.id}
              label={d.label}
              sub={`${d.unit} · ${d.hint}`}
              suggested={formatRange(suggested.deliverables[d.id])}
              custom={e.custom}
              value={e.custom ? e.low : 0}
              onCustom={(on) =>
                setDeliverable(
                  d.id,
                  on
                    ? {
                        custom: true,
                        low: suggested.deliverables[d.id].high,
                        high: suggested.deliverables[d.id].high,
                      }
                    : { custom: false },
                )
              }
              onValue={(n) => setDeliverable(d.id, { low: n, high: n })}
            />
          )
        })}
      </ul>

      {/* Anything else they sell, priced by them */}
      <div className="pt-2">
        <p className="font-bold text-foreground">Your own items</p>
        <p className="text-sm text-gray-600">
          Anything else brands can book from you: an autograph signing, a camp visit, a podcast
          mention. Each one shows in your Content Shop with a Book this button.
        </p>
      </div>
      {draft.items.length ? (
        <ul className="space-y-3">
          {draft.items.map((it, i) => {
            const set = (patch: Partial<Item>) =>
              setDraft(
                (d) =>
                  d && { ...d, items: d.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) },
              )
            return (
              <li
                key={it.id ?? `new-${i}`}
                className="space-y-2 rounded-2xl border border-foreground/10 bg-card p-4 shadow-[0_1px_2px_rgba(30,41,59,0.04)]"
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={it.label}
                    onChange={(e) => set({ label: e.target.value })}
                    placeholder="What it is, e.g. Autograph signing"
                    aria-label="Item name"
                    maxLength={60}
                    className={INPUT_CLASSES.dark}
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${it.label || 'this item'}`}
                    onClick={() =>
                      setDraft((d) => d && { ...d, items: d.items.filter((_, j) => j !== i) })
                    }
                    className="shrink-0 rounded-md p-2 text-gray-600 hover:bg-foreground/10"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <Input
                  value={it.description}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="Details brands should know (optional)"
                  aria-label="Item details"
                  maxLength={200}
                  className={INPUT_CLASSES.dark}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    inputMode="numeric"
                    value={it.low || ''}
                    onChange={(e) => {
                      const n = Math.max(0, Math.round(Number(e.target.value) || 0))
                      set({ low: n, high: n })
                    }}
                    placeholder="Price, $"
                    aria-label={`${it.label || 'Item'} price`}
                    className={INPUT_CLASSES.dark}
                  />
                  <select
                    value={it.unit}
                    onChange={(e) => set({ unit: e.target.value as ItemUnit })}
                    aria-label="How it's priced"
                    className="h-10 rounded-md border border-foreground/10 bg-card px-2 text-base text-foreground"
                  >
                    {ITEM_UNITS.map((u) => (
                      <option key={u} value={u} className="text-black">
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            )
          })}
        </ul>
      ) : null}
      {draft.items.length < MAX_ITEMS ? (
        <button
          type="button"
          onClick={() =>
            setDraft(
              (d) =>
                d && {
                  ...d,
                  items: [
                    ...d.items,
                    { label: '', description: '', unit: 'each', low: 0, high: 0 },
                  ],
                },
            )
          }
          className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-dashed border-[#C0FD72]/60 px-4 text-sm font-semibold text-[#4d7c0f] hover:bg-foreground/5"
        >
          <Plus className="h-4 w-4" /> Add an item
        </button>
      ) : null}

      <div className="pt-2">
        <p className="font-bold text-foreground">Running your posts as ads</p>
        <p className="text-sm text-gray-600">
          Every deal includes {INCLUDED_LICENSE_DAYS} days for the brand to run your content as a
          paid ad. It's part of your prices above, so there's nothing extra to agree.
        </p>
      </div>
      <div className="rounded-2xl border border-[#C0FD72]/30 bg-[#C0FD72]/10 px-4 py-3 text-sm text-gray-800">
        <span className="font-bold text-[#4d7c0f]">{INCLUDED_LICENSE_DAYS} days included</span>{' '}
        with every deal
      </div>
      <div>
        <p className="font-bold text-foreground">Extensions</p>
        <p className="text-sm text-gray-600">
          What a brand pays per video to keep running it longer. Longer extensions get a better
          monthly rate, so brands are more likely to pick them.
        </p>
      </div>
      <ul className="space-y-3">
        {LICENSE_TERMS.map((t) => {
          const e = draft.licensing[t.days]
          return (
            <PriceRow
              key={t.days}
              label={t.label}
              sub={
                savings(t.days) > 0
                  ? `per video · ${Math.round(savings(t.days) * 100)}% off the monthly rate`
                  : 'per video · your monthly rate'
              }
              suggestedNote="Suggested from your Reel price"
              suggested={`$${licenseSuggestion[t.days].toLocaleString('en-US')}`}
              custom={e.custom}
              value={e.custom ? e.amount : 0}
              onCustom={(on) =>
                setLicense(
                  t.days,
                  on ? { custom: true, amount: licenseSuggestion[t.days] } : { custom: false },
                )
              }
              onValue={(n) => setLicense(t.days, { amount: n })}
            />
          )
        })}
      </ul>
    </SetupShell>
  )
}

export default RateCardPage
